import { useState, useEffect, useCallback } from "react";
import { Tabs, Tab, styled } from "@mui/material";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import { useNavigate, useLocation } from "react-router-dom";
import DocumentsTable from "./components/DocumentsTable";
import { useDispatch, useSelector } from "react-redux";
import { AppThunkDispatch, RootState } from "@/store";
import { UserTierType } from "@/constants/enums/user-tier-type.enum";
import { UserDocumentTabType } from "@/constants/enums/user-document-tab-type.enum";
import actionsHandler from "@/store/actions/actionHandler";
import MyDriveTable from "./components/MyDriveTable";
import IMDocumentTable from "./components/IMDocuments";
import TermsSheetTable from "./components/TermSheetDocuments";
import CustomLoader from "@/components/common/loader/SpinnerLoader";
import { DocumentsPermissions } from "@/constants/permission-modules/base-permissions";
import { usePermissionState } from "@/providers/PermissionProvider";

const StyledTabs = styled(Tabs)({
  minHeight: "unset",
  "& .MuiTabs-indicator": {
    display: "none",
  },
});

const StyledTab = styled(Tab)({
  minHeight: "unset",
  padding: "8px 16px",
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 600,
  minWidth: "unset",
});

interface UserTierTab {
  label: string;
  path: string;
  type: UserDocumentTabType;
}

type UserTierTabs = {
  [key in UserTierType]: UserTierTab[];
};

const tabPermissions: Record<UserDocumentTabType, DocumentsPermissions> = {
  [UserDocumentTabType.imDocument]: DocumentsPermissions.VIEW_IM_DOCUMENTS,
  [UserDocumentTabType.termsSheet]:
    DocumentsPermissions.VIEW_TERMS_SHEET_DOCUMENTS,
  [UserDocumentTabType.myDrive]: DocumentsPermissions.VIEW_MY_DRIVE_DOCUMENTS,
  [UserDocumentTabType.documentsToBeApproved]:
    DocumentsPermissions.VIEW_DOCUMENTS_TO_BE_APPROVED,
  [UserDocumentTabType.other]:
    DocumentsPermissions.VIEW_DOCUMENTS_TO_BE_APPROVED,
  [UserDocumentTabType.financialDocs]:
    DocumentsPermissions.VIEW_DOCUMENTS_TO_BE_APPROVED,
  [UserDocumentTabType.legalDocs]:
    DocumentsPermissions.VIEW_DOCUMENTS_TO_BE_APPROVED,
};

const userTierTabs: UserTierTabs = {
  [UserTierType.Tier1]: [
    {
      label: "IM Document",
      path: "/document/im-document",
      type: UserDocumentTabType.imDocument,
    },
    {
      label: "Terms Sheet Document",
      path: "/document/terms-sheet",
      type: UserDocumentTabType.termsSheet,
    },
    {
      label: "My Drive",
      path: "/document/my-drive",
      type: UserDocumentTabType.myDrive,
    },
  ],
  [UserTierType.Tier2]: [
    {
      label: "IM Document",
      path: "/document/im-document",
      type: UserDocumentTabType.imDocument,
    },
    {
      label: "Terms Sheet Document",
      path: "/document/terms-sheet",
      type: UserDocumentTabType.termsSheet,
    },
    {
      label: "My Drive",
      path: "/document/my-drive",
      type: UserDocumentTabType.myDrive,
    },
  ],
  [UserTierType.Tier3]: [
    {
      label: "Documents to be Approved",
      path: "/document/to-be-approved",
      type: UserDocumentTabType.documentsToBeApproved,
    },
    {
      label: "IM Document",
      path: "/document/im-document",
      type: UserDocumentTabType.imDocument,
    },
    {
      label: "Terms Sheet Document",
      path: "/document/terms-sheet",
      type: UserDocumentTabType.termsSheet,
    },
    {
      label: "My Drive",
      path: "/document/my-drive",
      type: UserDocumentTabType.myDrive,
    },
  ],
  [UserTierType.Tier4]: [
    {
      label: "Terms Sheet Document",
      path: "/document/terms-sheet",
      type: UserDocumentTabType.termsSheet,
    },
    {
      label: "My Drive",
      path: "/document/my-drive",
      type: UserDocumentTabType.myDrive,
    },
  ],
  [UserTierType.Tier5]: [
    {
      label: "Documents to be Approved",
      path: "/document/to-be-approved",
      type: UserDocumentTabType.documentsToBeApproved,
    },
    {
      label: "IM Document",
      path: "/document/im-document",
      type: UserDocumentTabType.imDocument,
    },
    {
      label: "Terms Sheet Document",
      path: "/document/terms-sheet",
      type: UserDocumentTabType.termsSheet,
    },
    {
      label: "My Drive",
      path: "/document/my-drive",
      type: UserDocumentTabType.myDrive,
    },
  ],
  [UserTierType.Tier6]: [
    {
      label: "Documents to be Approved",
      path: "/document/to-be-approved",
      type: UserDocumentTabType.documentsToBeApproved,
    },
    {
      label: "IM Document",
      path: "/document/im-document",
      type: UserDocumentTabType.imDocument,
    },
    {
      label: "Terms Sheet Document",
      path: "/document/terms-sheet",
      type: UserDocumentTabType.termsSheet,
    },
    {
      label: "My Drive",
      path: "/document/my-drive",
      type: UserDocumentTabType.myDrive,
    },
  ],
  [UserTierType.Tier7]: [
    {
      label: "Documents to be Approved",
      path: "/document/to-be-approved",
      type: UserDocumentTabType.documentsToBeApproved,
    },
    {
      label: "IM Document",
      path: "/document/im-document",
      type: UserDocumentTabType.imDocument,
    },
    {
      label: "Terms Sheet Document",
      path: "/document/terms-sheet",
      type: UserDocumentTabType.termsSheet,
    },
    {
      label: "My Drive",
      path: "/document/my-drive",
      type: UserDocumentTabType.myDrive,
    },
  ],
};

const Documents = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppThunkDispatch>();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [forceRefresh, setForceRefresh] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userType, setUserType] = useState<UserTierType>(UserTierType.Tier1);
  const [availableTabs, setAvailableTabs] = useState<any[]>([]);
  const { isPermitted } = usePermissionState();
  const user = useSelector((state: RootState) => state.auth.user);
  const fetchTabs = useCallback(() => {
    return new Promise(async (resolve, reject) => {
      try {
        const { authenticatedCall: fetchTabs } = actionsHandler("createFolder");
        const response = await dispatch(
          fetchTabs({
            method: "get",
          }),
        );
        if (response.ok) {
          resolve(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching tabs:", error);
        reject(error);
      }
    });
  }, []);

  useEffect(() => {
    const initializeComponent = async () => {
      setIsLoading(true);
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const currentUserType = (user.user_type?.id ||
        UserTierType.Tier1) as UserTierType;
      setUserType(currentUserType);

      const currentPath = location.pathname;
      const tabs: any = await fetchTabs();
      const userTierAvailableTabs = userTierTabs[currentUserType].filter(
        (tab) => isPermitted(tabPermissions[tab.type]),
      );
      const availableTabs = userTierAvailableTabs.reduce(
        (acc: any, tab: any) => {
          const tabData = tabs.find((t: any) => t.folder_type === tab.type);
          if (tabData) {
            acc.push({ ...tab, ...tabData });
          }
          return acc || [];
        },
        [],
      );

      setAvailableTabs(availableTabs || []);
      const matchingTabIndex = availableTabs.findIndex(
        (tab: any) => tab.path === currentPath,
      );

      if (matchingTabIndex === -1) {
        if (availableTabs.length > 0) {
          navigate(availableTabs[0].path);
          setActiveTab(0);
        }
      } else {
        setActiveTab(matchingTabIndex);
      }

      setIsLoading(false);
    };

    initializeComponent();
  }, [user]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const tabs = userTierTabs[userType];
    if (tabs && tabs[newValue]) {
      setActiveTab(newValue);
      navigate(tabs[newValue].path);
    }
  };

  return (
    <div className="tw-relative">
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-6 tw-pt-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <img
                src={BackIcon}
                alt="Back Icon"
                onClick={() => navigate("/menu")}
                className="tw-cursor-pointer"
              />
              <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                Documents
              </h1>
            </div>
          </div>

          <div className="tw-w-fit tw-mt-4 tw-border tw-border-[#E9EAEB]">
            <StyledTabs value={activeTab} onChange={handleTabChange}>
              {availableTabs.map((tab, index) => (
                <StyledTab
                  key={tab.path}
                  label={tab.label}
                  className={`tw-text-sm tw-font-semibold ${
                    activeTab === index ? "active-tab" : "tw-text-[#232323]"
                  } tab-border`}
                />
              ))}
            </StyledTabs>
          </div>
        </div>
      </div>

      <div className="tw-mt-[177px]">
        {isLoading ? (
          <div className="tw-flex tw-justify-center tw-items-center tw-h-[200px]">
            <CustomLoader loading={true} isWrapper={true} size="50" />
          </div>
        ) : (
          activeTab >= 0 &&
          availableTabs[activeTab] &&
          (() => {
            switch (availableTabs[activeTab].type) {
              case UserDocumentTabType.myDrive:
                return (
                  <MyDriveTable
                    tabId={availableTabs[activeTab].id}
                    isNested={false}
                  />
                );
              case UserDocumentTabType.imDocument:
                return <IMDocumentTable tabId={availableTabs[activeTab].id} />;
              case UserDocumentTabType.termsSheet:
                return <TermsSheetTable tabId={availableTabs[activeTab].id} />;
              default:
                return (
                  <DocumentsTable
                    key={availableTabs[activeTab].type}
                    type={availableTabs[activeTab].type}
                    tabId={availableTabs[activeTab].id}
                  />
                );
            }
          })()
        )}
      </div>
    </div>
  );
};

export default Documents;
